import { useEffect, useState, type ReactNode } from 'react'
import {
  Alert, Badge, Button, Code, Group, Loader, Modal, Paper, PasswordInput, SegmentedControl, Select,
  Stack, Switch, Table, Text, TextInput,
} from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { IconAlertTriangle, IconInfoCircle, IconKey, IconLock } from '@tabler/icons-react'
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  PADRAO_LABEL_DE_CHAVE, PADRAO_USUARIO_DO_HSM, apagarChave, apagarUsuarioDoHsm, ativarCluster,
  compartilharChave, criarUsuarioDoHsm, erroDaGestao, gerarChave, listarChavesDoHsm,
  listarUsuariosDoHsm, problemaDaSenhaNova, trocarSenhaDoHsm,
  type ChaveDoHsm, type ChaveNova, type ClusterCloudHsm, type CredencialDoHsm, type CurvaEc,
  type PapelDoHsm, type UsuarioDoHsm,
} from '@/api/cloudhsm'
import type { ControleDaSessao } from './sessaoDoHsm'

/**
 * Gestão de usuários e chaves do HSM (F6, spec 2026-11-04 §3.2-§3.3).
 *
 * Cada ação loga no HSM com um papel: usuários com o admin, chaves com o usuário cripto. Por
 * isso há duas sessões, abertas à parte (`sessaoDoHsm.ts`): a credencial fica só na memória da
 * aba por 10 minutos. A permissão do ConsoleAI (`cloudhsm:manage`, `cloudhsm:delete`) esconde
 * os botões; o backend confere de novo. Erro do executor (HSM recusou, 1 HSM só, HSM
 * inalcançável) aparece como veio.
 */

const CHAVES_POR_PAGINA = 100

const NOME_DO_PAPEL: Record<string, string> = {
  admin: 'admin',
  'crypto-user': 'usuário cripto',
  'unactivated-admin': 'admin não ativado',
}

interface Alvo {
  tenantId: number
  cluster: ClusterCloudHsm
}

interface Permissoes {
  podeGerir: boolean
  podeApagar: boolean
}

function mmss(ms: number): string {
  const s = Math.ceil(ms / 1000)
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`
}

// ── sessão ──────────────────────────────────────────────────────────────────

function BarraDaSessao({ papel, controle }: { papel: PapelDoHsm; controle: ControleDaSessao }) {
  const [abrindo, setAbrindo] = useState(false)
  const { sessao, restanteMs, encerrar } = controle
  const nome = NOME_DO_PAPEL[papel]

  if (sessao) {
    return (
      <Paper withBorder p="xs" radius="sm">
        <Group justify="space-between">
          <Group gap="xs">
            <IconLock size={16} />
            <Text size="sm">
              Logado no HSM como <Code>{sessao.credencial.usuario}</Code> ({nome})
            </Text>
            <Badge variant="light" color={restanteMs < 60_000 ? 'orange' : 'blue'} aria-label="tempo da sessão">
              expira em {mmss(restanteMs)}
            </Badge>
          </Group>
          <Button size="xs" variant="default" onClick={encerrar}>Encerrar</Button>
        </Group>
      </Paper>
    )
  }

  return (
    <>
      <Group>
        <Button size="xs" variant="light" leftSection={<IconLock size={14} />} onClick={() => setAbrindo(true)}>
          Abrir gestão como {nome}
        </Button>
      </Group>
      <ModalDaCredencial
        opened={abrindo}
        papel={papel}
        onClose={() => setAbrindo(false)}
        onAbrir={(c) => { controle.abrir(c); setAbrindo(false) }}
      />
    </>
  )
}

function ModalDaCredencial({ opened, papel, onClose, onAbrir }: {
  opened: boolean
  papel: PapelDoHsm
  onClose: () => void
  onAbrir: (c: CredencialDoHsm) => void
}) {
  const [usuario, setUsuario] = useState('')
  const [senha, setSenha] = useState('')
  useEffect(() => { setUsuario(''); setSenha('') }, [opened])

  const usuarioOk = PADRAO_USUARIO_DO_HSM.test(usuario)
  const pronto = usuarioOk && senha.length > 0 && senha.length <= 128

  return (
    <Modal opened={opened} onClose={onClose} title={`Abrir gestão como ${NOME_DO_PAPEL[papel]} do HSM`}>
      <form onSubmit={(e) => { e.preventDefault(); if (pronto) onAbrir({ role: papel, usuario, senha }) }}>
        <Stack gap="sm">
          <Text size="sm" c="dimmed">
            A credencial fica só na memória desta aba por 10 minutos e vai junto em cada operação.
            O ConsoleAI não a grava. Fechar a aba, sair da página ou clicar em Encerrar a descarta.
          </Text>
          <TextInput
            label="Usuário do HSM"
            description={`Um ${NOME_DO_PAPEL[papel]} do HSM.`}
            value={usuario}
            onChange={(e) => setUsuario(e.currentTarget.value)}
            error={usuario && !usuarioOk ? 'Até 31 letras, números ou _.' : null}
            autoComplete="off"
            data-autofocus
          />
          <PasswordInput
            label="Senha do HSM"
            value={senha}
            onChange={(e) => setSenha(e.currentTarget.value)}
            autoComplete="off"
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={onClose}>Cancelar</Button>
            <Button type="submit" disabled={!pronto}>Abrir</Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  )
}

// ── partes comuns dos modais ────────────────────────────────────────────────

function Rodape({ erro, pendente, pronto, rotulo, cor, onCancelar, onConfirmar }: {
  erro: string | null
  pendente: boolean
  pronto: boolean
  rotulo: string
  cor?: string
  onCancelar: () => void
  onConfirmar: () => void
}) {
  return (
    <>
      {erro && <Alert color="red" icon={<IconAlertTriangle size={16} />}>{erro}</Alert>}
      <Group justify="flex-end">
        <Button variant="default" onClick={onCancelar} disabled={pendente}>Cancelar</Button>
        <Button color={cor} onClick={onConfirmar} disabled={!pronto} loading={pendente}>{rotulo}</Button>
      </Group>
    </>
  )
}

/** Senha nova com confirmação: o HSM grava o que for digitado, e ninguém a recupera depois. */
function SenhaNova({ senha, confirmacao, onSenha, onConfirmacao, rotulo = 'Senha nova' }: {
  senha: string
  confirmacao: string
  onSenha: (v: string) => void
  onConfirmacao: (v: string) => void
  rotulo?: string
}) {
  const problema = senha ? problemaDaSenhaNova(senha) : null
  return (
    <>
      <PasswordInput label={rotulo} value={senha} onChange={(e) => onSenha(e.currentTarget.value)}
        error={problema} autoComplete="new-password" />
      <PasswordInput label="Repita a senha" value={confirmacao}
        onChange={(e) => onConfirmacao(e.currentTarget.value)}
        error={confirmacao && confirmacao !== senha ? 'As senhas não conferem.' : null}
        autoComplete="new-password" />
    </>
  )
}

const senhaPronta = (senha: string, confirmacao: string) => !problemaDaSenhaNova(senha) && senha === confirmacao

/** O que muda o HSM: a mensagem de erro fica no modal, e o sucesso fecha e avisa. */
function useAcaoNoHsm(acao: () => Promise<unknown>, falha: string, sucesso: string, onFeito: () => void) {
  const [erro, setErro] = useState<string | null>(null)
  const mut = useMutation({
    mutationFn: acao,
    onMutate: () => setErro(null),
    onSuccess: () => { notifications.show({ color: 'green', message: sucesso }); onFeito() },
    onError: (e) => setErro(erroDaGestao(e, falha)),
  })
  return { erro, setErro, mut }
}

function ConfirmacaoDigitada({ esperado, valor, onChange, oQue }: {
  esperado: string
  valor: string
  onChange: (v: string) => void
  oQue: string
}) {
  return (
    <TextInput
      label={`Para confirmar, digite ${oQue}`}
      description={<>Exatamente como está: <Code>{esperado}</Code></>}
      value={valor}
      onChange={(e) => onChange(e.currentTarget.value)}
      error={valor && valor !== esperado ? 'Não confere.' : null}
      autoComplete="off"
      data-autofocus
    />
  )
}

// ── usuários ────────────────────────────────────────────────────────────────

export function UsuariosDoHsm({ tenantId, cluster, sessao, podeGerir, podeApagar }:
  Alvo & Permissoes & { sessao: ControleDaSessao }) {
  const qc = useQueryClient()
  const chave = ['cloudhsm', 'usuarios', tenantId, cluster.regiao, cluster.cluster_id]
  const q = useQuery({
    queryKey: chave,
    queryFn: () => listarUsuariosDoHsm(tenantId, cluster.cluster_id, cluster.regiao),
    retry: false,
    staleTime: 30_000,
  })
  const atualizar = () => qc.invalidateQueries({ queryKey: chave })

  const [ativando, setAtivando] = useState(false)
  const [criando, setCriando] = useState(false)
  const [trocando, setTrocando] = useState<UsuarioDoHsm | null>(null)
  const [apagando, setApagando] = useState<UsuarioDoHsm | null>(null)

  const cred = sessao.sessao?.credencial ?? null
  const gerencia = podeGerir || podeApagar

  return (
    <Stack gap="sm">
      {cluster.estado === 'INITIALIZED' && podeGerir && (
        <Alert color="yellow" icon={<IconInfoCircle size={16} />} title="Cluster inicializado e ainda não ativado">
          <Stack gap="xs">
            <Text size="sm">
              Ativar grava a senha do admin do HSM. Guarde-a: o ConsoleAI não a grava e não a recupera.
            </Text>
            <Group><Button size="xs" onClick={() => setAtivando(true)}>Ativar cluster</Button></Group>
          </Stack>
        </Alert>
      )}

      <Text size="sm" c="dimmed">
        Usuários que o HSM tem (user list, sem login). Criar, trocar senha e apagar exigem o login de
        um admin do HSM.
      </Text>

      {gerencia ? (
        <BarraDaSessao papel="admin" controle={sessao} />
      ) : (
        <Text size="sm" c="dimmed">
          Sem as permissões cloudhsm:manage e cloudhsm:delete neste tenant: só a lista.
        </Text>
      )}

      <Group gap="xs">
        {cred && podeGerir && <Button size="xs" onClick={() => setCriando(true)}>Novo usuário</Button>}
        <Button size="xs" variant="subtle" onClick={atualizar} loading={q.isFetching}>Atualizar</Button>
      </Group>

      {q.isLoading && <Loader size="sm" />}
      {q.isError && (
        <Alert color="red" icon={<IconAlertTriangle size={16} />}>
          {erroDaGestao(q.error, 'Não foi possível listar os usuários do HSM.')}
        </Alert>
      )}
      {q.data && q.data.length === 0 && <Text size="sm" c="dimmed">O HSM não devolveu usuário.</Text>}
      {q.data && q.data.length > 0 && (
        <Table striped withTableBorder>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Usuário</Table.Th><Table.Th>Papel</Table.Th><Table.Th>Bloqueado</Table.Th>
              <Table.Th>Cobertura</Table.Th><Table.Th />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {q.data.map((u) => {
              const papelGerivel = u.role === 'admin' || u.role === 'crypto-user'
              return (
                <Table.Tr key={u.username}>
                  <Table.Td ff="monospace">{u.username}</Table.Td>
                  <Table.Td>{NOME_DO_PAPEL[u.role] ?? u.role}</Table.Td>
                  <Table.Td>{u.bloqueado ? <Badge color="red" variant="light">bloqueado</Badge> : 'não'}</Table.Td>
                  <Table.Td>{u.cobertura ?? '—'}</Table.Td>
                  <Table.Td>
                    {cred && papelGerivel && (
                      <Group gap={4} justify="flex-end" wrap="nowrap">
                        {podeGerir && (
                          <Button size="compact-xs" variant="default" onClick={() => setTrocando(u)}
                            aria-label={`Trocar senha de ${u.username}`}>
                            Trocar senha
                          </Button>
                        )}
                        {podeApagar && (
                          <Button size="compact-xs" color="red" variant="light" onClick={() => setApagando(u)}
                            aria-label={`Apagar ${u.username}`}>
                            Apagar
                          </Button>
                        )}
                      </Group>
                    )}
                  </Table.Td>
                </Table.Tr>
              )
            })}
          </Table.Tbody>
        </Table>
      )}

      {podeGerir && (
        <ModalAtivar opened={ativando} onClose={() => setAtivando(false)} tenantId={tenantId} cluster={cluster}
          onFeito={() => { setAtivando(false); atualizar(); qc.invalidateQueries({ queryKey: ['cloudhsm', 'clusters'] }) }} />
      )}
      {cred && podeGerir && (
        <ModalNovoUsuario opened={criando} onClose={() => setCriando(false)} tenantId={tenantId}
          cluster={cluster} credencial={cred} onFeito={() => { setCriando(false); atualizar() }} />
      )}
      {cred && podeGerir && trocando && (
        <ModalTrocarSenha usuario={trocando} onClose={() => setTrocando(null)} tenantId={tenantId}
          cluster={cluster} credencial={cred} onFeito={() => setTrocando(null)} />
      )}
      {cred && podeApagar && apagando && (
        <ModalApagarUsuario usuario={apagando} onClose={() => setApagando(null)} tenantId={tenantId}
          cluster={cluster} credencial={cred} onFeito={() => { setApagando(null); atualizar() }} />
      )}
    </Stack>
  )
}

function ModalAtivar({ opened, onClose, onFeito, tenantId, cluster }: Alvo & {
  opened: boolean
  onClose: () => void
  onFeito: () => void
}) {
  const [senha, setSenha] = useState('')
  const [confirmacao, setConfirmacao] = useState('')
  const { erro, setErro, mut } = useAcaoNoHsm(
    () => ativarCluster(tenantId, cluster.cluster_id, cluster.regiao, senha),
    'Não foi possível ativar o cluster.', 'Cluster ativado. A senha do admin é a que você digitou.', onFeito)
  useEffect(() => { setSenha(''); setConfirmacao(''); setErro(null) }, [opened, setErro])

  return (
    <Modal opened={opened} onClose={onClose} title={`Ativar ${cluster.cluster_id}`}>
      <Stack gap="sm">
        <Text size="sm">
          A ativação grava a senha do admin do HSM. O ConsoleAI não a grava e não a recupera.
        </Text>
        <SenhaNova rotulo="Senha do admin" senha={senha} confirmacao={confirmacao}
          onSenha={setSenha} onConfirmacao={setConfirmacao} />
        <Rodape erro={erro} pendente={mut.isPending} pronto={senhaPronta(senha, confirmacao)}
          rotulo="Ativar" onCancelar={onClose} onConfirmar={() => mut.mutate()} />
      </Stack>
    </Modal>
  )
}

function ModalNovoUsuario({ opened, onClose, onFeito, tenantId, cluster, credencial }: Alvo & {
  opened: boolean
  onClose: () => void
  onFeito: () => void
  credencial: CredencialDoHsm
}) {
  const [username, setUsername] = useState('')
  const [papel, setPapel] = useState<PapelDoHsm>('crypto-user')
  const [senha, setSenha] = useState('')
  const [confirmacao, setConfirmacao] = useState('')
  const { erro, setErro, mut } = useAcaoNoHsm(
    () => criarUsuarioDoHsm(tenantId, cluster.cluster_id, cluster.regiao, credencial,
      { username, role: papel, senha }),
    'Não foi possível criar o usuário.', `Usuário ${username} criado no HSM.`, onFeito)
  useEffect(() => {
    setUsername(''); setPapel('crypto-user'); setSenha(''); setConfirmacao(''); setErro(null)
  }, [opened, setErro])

  const nomeOk = PADRAO_USUARIO_DO_HSM.test(username)
  return (
    <Modal opened={opened} onClose={onClose} title="Novo usuário do HSM">
      <Stack gap="sm">
        <TextInput label="Nome" value={username} onChange={(e) => setUsername(e.currentTarget.value)}
          error={username && !nomeOk ? 'Até 31 letras, números ou _.' : null} autoComplete="off" data-autofocus />
        <Select label="Papel" value={papel} allowDeselect={false}
          onChange={(v) => v && setPapel(v as PapelDoHsm)}
          data={[{ value: 'crypto-user', label: 'usuário cripto (gera e usa chaves)' },
            { value: 'admin', label: 'admin (gere usuários)' }]} />
        <SenhaNova senha={senha} confirmacao={confirmacao} onSenha={setSenha} onConfirmacao={setConfirmacao} />
        <Rodape erro={erro} pendente={mut.isPending} pronto={nomeOk && senhaPronta(senha, confirmacao)}
          rotulo="Criar" onCancelar={onClose} onConfirmar={() => mut.mutate()} />
      </Stack>
    </Modal>
  )
}

function ModalTrocarSenha({ usuario, onClose, onFeito, tenantId, cluster, credencial }: Alvo & {
  usuario: UsuarioDoHsm
  onClose: () => void
  onFeito: () => void
  credencial: CredencialDoHsm
}) {
  const [senha, setSenha] = useState('')
  const [confirmacao, setConfirmacao] = useState('')
  const { erro, mut } = useAcaoNoHsm(
    () => trocarSenhaDoHsm(tenantId, cluster.cluster_id, cluster.regiao, credencial,
      usuario.username, usuario.role as PapelDoHsm, senha),
    'Não foi possível trocar a senha.', `Senha de ${usuario.username} trocada.`, onFeito)

  return (
    <Modal opened onClose={onClose} title={`Trocar a senha de ${usuario.username}`}>
      <Stack gap="sm">
        <SenhaNova senha={senha} confirmacao={confirmacao} onSenha={setSenha} onConfirmacao={setConfirmacao} />
        <Rodape erro={erro} pendente={mut.isPending} pronto={senhaPronta(senha, confirmacao)}
          rotulo="Trocar senha" onCancelar={onClose} onConfirmar={() => mut.mutate()} />
      </Stack>
    </Modal>
  )
}

function ModalApagarUsuario({ usuario, onClose, onFeito, tenantId, cluster, credencial }: Alvo & {
  usuario: UsuarioDoHsm
  onClose: () => void
  onFeito: () => void
  credencial: CredencialDoHsm
}) {
  const [digitado, setDigitado] = useState('')
  const { erro, mut } = useAcaoNoHsm(
    () => apagarUsuarioDoHsm(tenantId, cluster.cluster_id, cluster.regiao, credencial,
      usuario.username, usuario.role as PapelDoHsm, digitado),
    'Não foi possível apagar o usuário.', `Usuário ${usuario.username} apagado do HSM.`, onFeito)

  return (
    <Modal opened onClose={onClose} title={`Apagar ${usuario.username}`}>
      <Stack gap="sm">
        <Alert color="red" title="Esta ação é irreversível">
          O usuário sai de todos os HSMs do cluster. O HSM recusa apagar usuário que ainda tem chave.
        </Alert>
        <ConfirmacaoDigitada esperado={usuario.username} valor={digitado} onChange={setDigitado}
          oQue="o nome do usuário" />
        <Rodape erro={erro} pendente={mut.isPending} pronto={digitado === usuario.username}
          rotulo="Apagar usuário" cor="red" onCancelar={onClose} onConfirmar={() => mut.mutate()} />
      </Stack>
    </Modal>
  )
}

// ── chaves ──────────────────────────────────────────────────────────────────

export function ChavesDoHsm({ tenantId, cluster, sessao, podeGerir, podeApagar }:
  Alvo & Permissoes & { sessao: ControleDaSessao }) {
  return (
    <Stack gap="sm">
      <Alert color="blue" variant="light" icon={<IconInfoCircle size={16} />}>
        A lista mostra só as chaves que o usuário cripto logado vê: as dele e as compartilhadas com
        ele. O admin do HSM só vê a referência das chaves dos usuários cripto, sem label nem
        atributos; gerar, compartilhar e apagar é agir como o usuário cripto dono da chave.
      </Alert>
      <BarraDaSessao papel="crypto-user" controle={sessao} />
      {sessao.sessao && (
        <ListaDeChaves tenantId={tenantId} cluster={cluster} sessaoId={sessao.sessao.id}
          credencial={sessao.sessao.credencial} podeGerir={podeGerir} podeApagar={podeApagar} />
      )}
    </Stack>
  )
}

type Partilha = { chave: ChaveDoHsm; compartilhar: boolean }

function ListaDeChaves({ tenantId, cluster, sessaoId, credencial, podeGerir, podeApagar }: Alvo & Permissoes & {
  sessaoId: number
  credencial: CredencialDoHsm
}) {
  const qc = useQueryClient()
  // A chave da query leva o id da sessão, nunca a credencial; e o cache morre com a tela.
  const chave = ['cloudhsm', 'chaves', tenantId, cluster.regiao, cluster.cluster_id, sessaoId]
  const q = useInfiniteQuery({
    queryKey: chave,
    queryFn: ({ pageParam }) => listarChavesDoHsm(tenantId, cluster.cluster_id, cluster.regiao, credencial,
      { max_items: CHAVES_POR_PAGINA, starting_token: pageParam }),
    initialPageParam: null as string | null,
    getNextPageParam: (p) => p.proximo,
    retry: false,
    gcTime: 0,
  })
  const atualizar = () => qc.invalidateQueries({ queryKey: chave })

  const [gerando, setGerando] = useState(false)
  const [partilha, setPartilha] = useState<Partilha | null>(null)
  const [apagando, setApagando] = useState<ChaveDoHsm | null>(null)

  const chaves = q.data?.pages.flatMap((p) => p.chaves) ?? []
  const total = q.data?.pages[0]?.total ?? null

  return (
    <Stack gap="sm">
      <Group gap="xs">
        {podeGerir && (
          <Button size="xs" leftSection={<IconKey size={14} />} onClick={() => setGerando(true)}>Gerar chave</Button>
        )}
        <Button size="xs" variant="subtle" onClick={atualizar} loading={q.isFetching && !q.isFetchingNextPage}>
          Atualizar
        </Button>
        {!podeGerir && !podeApagar && (
          <Text size="sm" c="dimmed">Sem as permissões cloudhsm:manage e cloudhsm:delete: só a lista.</Text>
        )}
      </Group>

      {q.isLoading && <Loader size="sm" />}
      {q.isError && (
        <Alert color="red" icon={<IconAlertTriangle size={16} />}>
          {erroDaGestao(q.error, 'Não foi possível listar as chaves.')}
        </Alert>
      )}
      {q.data && chaves.length === 0 && (
        <Text size="sm" c="dimmed">O usuário {credencial.usuario} não vê nenhuma chave neste HSM.</Text>
      )}
      {chaves.length > 0 && (
        <>
          {total !== null && (
            <Text size="xs" c="dimmed">{chaves.length} de {total} chave(s).</Text>
          )}
          <Table striped withTableBorder>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Referência</Table.Th><Table.Th>Label</Table.Th><Table.Th>Tipo</Table.Th>
                <Table.Th>Extraível</Table.Th><Table.Th>Dono</Table.Th><Table.Th>Compartilhada com</Table.Th>
                <Table.Th />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {chaves.map((k) => {
                // Só o dono compartilha e apaga (spec §3.2); sem a informação, o HSM decide.
                const dono = k.donos.length === 0 || k.donos.includes(credencial.usuario)
                return (
                  <Table.Tr key={k.referencia}>
                    <Table.Td ff="monospace">{k.referencia}</Table.Td>
                    <Table.Td ff="monospace">{k.label ?? '—'}</Table.Td>
                    <Table.Td>{[k.tipo, k.classe].filter(Boolean).join(' · ') || '—'}</Table.Td>
                    <Table.Td>
                      {k.extraivel === null ? '—' : k.extraivel
                        ? <Badge color="orange" variant="light">sim</Badge> : 'não'}
                    </Table.Td>
                    <Table.Td ff="monospace">{k.donos.join(', ') || '—'}</Table.Td>
                    <Table.Td ff="monospace">{k.compartilhada_com.join(', ') || '—'}</Table.Td>
                    <Table.Td>
                      {dono && (
                        <Group gap={4} justify="flex-end" wrap="nowrap">
                          {podeGerir && (
                            <Button size="compact-xs" variant="default"
                              onClick={() => setPartilha({ chave: k, compartilhar: true })}>
                              Compartilhar
                            </Button>
                          )}
                          {podeGerir && k.compartilhada_com.length > 0 && (
                            <Button size="compact-xs" variant="default"
                              onClick={() => setPartilha({ chave: k, compartilhar: false })}>
                              Descompartilhar
                            </Button>
                          )}
                          {podeApagar && (
                            <Button size="compact-xs" color="red" variant="light" onClick={() => setApagando(k)}
                              aria-label={`Apagar a chave ${k.label ?? k.referencia}`}>
                              Apagar
                            </Button>
                          )}
                        </Group>
                      )}
                    </Table.Td>
                  </Table.Tr>
                )
              })}
            </Table.Tbody>
          </Table>
          {q.hasNextPage && (
            <Group>
              <Button size="xs" variant="default" onClick={() => q.fetchNextPage()} loading={q.isFetchingNextPage}>
                Carregar mais
              </Button>
            </Group>
          )}
        </>
      )}

      {podeGerir && (
        <ModalGerarChave opened={gerando} onClose={() => setGerando(false)} tenantId={tenantId}
          cluster={cluster} credencial={credencial} onFeito={() => { setGerando(false); atualizar() }} />
      )}
      {podeGerir && partilha && (
        <ModalPartilha partilha={partilha} onClose={() => setPartilha(null)} tenantId={tenantId}
          cluster={cluster} credencial={credencial} onFeito={() => { setPartilha(null); atualizar() }} />
      )}
      {podeApagar && apagando && (
        <ModalApagarChave chave={apagando} onClose={() => setApagando(null)} tenantId={tenantId}
          cluster={cluster} credencial={credencial} onFeito={() => { setApagando(null); atualizar() }} />
      )}
    </Stack>
  )
}

function CampoDeLabel({ rotulo, valor, onChange, autoFocus }: {
  rotulo: string
  valor: string
  onChange: (v: string) => void
  autoFocus?: boolean
}) {
  return (
    <TextInput label={rotulo} value={valor} onChange={(e) => onChange(e.currentTarget.value)}
      error={valor && !PADRAO_LABEL_DE_CHAVE.test(valor)
        ? 'Até 127 caracteres: letras, números, _, . e -, sem começar por . ou -.' : null}
      autoComplete="off" data-autofocus={autoFocus || undefined} />
  )
}

function ModalGerarChave({ opened, onClose, onFeito, tenantId, cluster, credencial }: Alvo & {
  opened: boolean
  onClose: () => void
  onFeito: () => void
  credencial: CredencialDoHsm
}) {
  const [algoritmo, setAlgoritmo] = useState<ChaveNova['algoritmo']>('aes')
  const [label, setLabel] = useState('')
  const [publico, setPublico] = useState('')
  const [privado, setPrivado] = useState('')
  const [bytes, setBytes] = useState('32')
  const [bits, setBits] = useState('2048')
  const [curva, setCurva] = useState<CurvaEc>('prime256v1')
  const [extraivel, setExtraivel] = useState(false)

  const nova = (): ChaveNova => {
    if (algoritmo === 'aes') {
      return { algoritmo, label, key_length_bytes: Number(bytes) as 16 | 24 | 32, extractable: extraivel }
    }
    if (algoritmo === 'rsa') {
      return { algoritmo, public_label: publico, private_label: privado,
        modulus_size_bits: Number(bits) as 2048 | 3072 | 4096, extractable: extraivel }
    }
    return { algoritmo, public_label: publico, private_label: privado, curve: curva, extractable: extraivel }
  }
  const { erro, setErro, mut } = useAcaoNoHsm(
    () => gerarChave(tenantId, cluster.cluster_id, cluster.regiao, credencial, nova()),
    'Não foi possível gerar a chave.', 'Chave gerada no HSM.', onFeito)
  useEffect(() => {
    setAlgoritmo('aes'); setLabel(''); setPublico(''); setPrivado(''); setBytes('32'); setBits('2048')
    setCurva('prime256v1'); setExtraivel(false); setErro(null)
  }, [opened, setErro])

  const pronto = algoritmo === 'aes'
    ? PADRAO_LABEL_DE_CHAVE.test(label)
    : PADRAO_LABEL_DE_CHAVE.test(publico) && PADRAO_LABEL_DE_CHAVE.test(privado)

  return (
    <Modal opened={opened} onClose={onClose} title="Gerar chave" size="lg">
      <Stack gap="sm">
        <Text size="sm" c="dimmed">
          A chave nasce no HSM como de {credencial.usuario}, o usuário cripto logado.
        </Text>
        <SegmentedControl value={algoritmo} onChange={(v) => setAlgoritmo(v as ChaveNova['algoritmo'])}
          data={[{ value: 'aes', label: 'AES (simétrica)' }, { value: 'rsa', label: 'Par RSA' },
            { value: 'ec', label: 'Par EC' }]} />
        {algoritmo === 'aes' ? (
          <>
            <CampoDeLabel rotulo="Label" valor={label} onChange={setLabel} autoFocus />
            <Select label="Tamanho" value={bytes} allowDeselect={false} onChange={(v) => v && setBytes(v)}
              data={[{ value: '16', label: 'AES-128' }, { value: '24', label: 'AES-192' },
                { value: '32', label: 'AES-256' }]} />
          </>
        ) : (
          <>
            <CampoDeLabel rotulo="Label da chave pública" valor={publico} onChange={setPublico} autoFocus />
            <CampoDeLabel rotulo="Label da chave privada" valor={privado} onChange={setPrivado} />
            {algoritmo === 'rsa' ? (
              <Select label="Módulo" value={bits} allowDeselect={false} onChange={(v) => v && setBits(v)}
                data={['2048', '3072', '4096'].map((b) => ({ value: b, label: `${b} bits` }))} />
            ) : (
              <Select label="Curva" value={curva} allowDeselect={false} onChange={(v) => v && setCurva(v as CurvaEc)}
                data={['prime256v1', 'secp256k1', 'secp384r1', 'secp521r1']} />
            )}
          </>
        )}
        <Switch
          label={algoritmo === 'aes' ? 'Extraível' : 'Chave privada extraível'}
          description="Extraível pode sair do HSM, embrulhada por outra chave. O padrão é não extraível."
          checked={extraivel}
          onChange={(e) => setExtraivel(e.currentTarget.checked)}
        />
        <Rodape erro={erro} pendente={mut.isPending} pronto={pronto} rotulo="Gerar"
          onCancelar={onClose} onConfirmar={() => mut.mutate()} />
      </Stack>
    </Modal>
  )
}

function ModalPartilha({ partilha, onClose, onFeito, tenantId, cluster, credencial }: Alvo & {
  partilha: Partilha
  onClose: () => void
  onFeito: () => void
  credencial: CredencialDoHsm
}) {
  const { chave, compartilhar } = partilha
  const [username, setUsername] = useState(compartilhar ? '' : chave.compartilhada_com[0] ?? '')
  const nome = chave.label ?? chave.referencia
  const { erro, mut } = useAcaoNoHsm(
    () => compartilharChave(tenantId, cluster.cluster_id, cluster.regiao, credencial,
      chave.referencia, username, compartilhar),
    compartilhar ? 'Não foi possível compartilhar a chave.' : 'Não foi possível descompartilhar a chave.',
    compartilhar ? `Chave ${nome} compartilhada com ${username}.` : `${username} não usa mais a chave ${nome}.`,
    onFeito)

  let campo: ReactNode
  if (compartilhar) {
    campo = (
      <TextInput label="Usuário cripto que vai usar a chave" value={username}
        onChange={(e) => setUsername(e.currentTarget.value)}
        description="Até 7 usuários cripto por chave. Quem recebe usa a chave, mas não apaga nem recompartilha."
        error={username && !PADRAO_USUARIO_DO_HSM.test(username) ? 'Até 31 letras, números ou _.' : null}
        autoComplete="off" data-autofocus />
    )
  } else {
    campo = (
      <Select label="Usuário que deixa de usar a chave" value={username} allowDeselect={false}
        onChange={(v) => v && setUsername(v)} data={chave.compartilhada_com} />
    )
  }

  return (
    <Modal opened onClose={onClose} title={`${compartilhar ? 'Compartilhar' : 'Descompartilhar'} ${nome}`}>
      <Stack gap="sm">
        {campo}
        <Rodape erro={erro} pendente={mut.isPending} pronto={PADRAO_USUARIO_DO_HSM.test(username)}
          rotulo={compartilhar ? 'Compartilhar' : 'Descompartilhar'} onCancelar={onClose}
          onConfirmar={() => mut.mutate()} />
      </Stack>
    </Modal>
  )
}

function ModalApagarChave({ chave, onClose, onFeito, tenantId, cluster, credencial }: Alvo & {
  chave: ChaveDoHsm
  onClose: () => void
  onFeito: () => void
  credencial: CredencialDoHsm
}) {
  // Chave sem label confirma pela referência, como o backend.
  const esperado = chave.label ?? chave.referencia
  const [digitado, setDigitado] = useState('')
  const { erro, mut } = useAcaoNoHsm(
    () => apagarChave(tenantId, cluster.cluster_id, cluster.regiao, credencial, chave.referencia, digitado),
    'Não foi possível apagar a chave.', `Chave ${esperado} apagada do HSM.`, onFeito)

  return (
    <Modal opened onClose={onClose} title={`Apagar a chave ${esperado}`}>
      <Stack gap="sm">
        <Alert color="red" title="Esta ação é irreversível">
          A chave some de todos os HSMs do cluster, e o que ela cifrou não decifra mais. Só um backup
          do cluster anterior à exclusão a traz de volta.
        </Alert>
        <ConfirmacaoDigitada esperado={esperado} valor={digitado} onChange={setDigitado}
          oQue={chave.label ? 'o label da chave' : 'a referência da chave'} />
        <Rodape erro={erro} pendente={mut.isPending} pronto={digitado === esperado}
          rotulo="Apagar chave" cor="red" onCancelar={onClose} onConfirmar={() => mut.mutate()} />
      </Stack>
    </Modal>
  )
}
